import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import api from '../services/api';
import type { AppDispatch, RootState } from '../store';
import {
    ContextOption,
    selectLocation,
    selectRestaurant,
    setLocationOptions,
    setRestaurants,
} from '../store/slices/activeLocationSlice';

export interface ActiveLocation {
    /** Selected restaurant. Admin picks it; everyone else gets their own. */
    restaurantId: string | null;
    /** Selected location; null means all locations (not offered to Admin). */
    locationId: string | null;
    restaurants: ContextOption[];
    locations: ContextOption[];
    isAdmin: boolean;
    setRestaurant: (id: string) => void;
    setLocation: (id: string | null) => void;
}

/** Reads the shell's context switcher. Stored in localStorage['qo.location']. */
export const useActiveLocation = (): ActiveLocation => {
    const dispatch = useDispatch<AppDispatch>();
    const slice = useSelector((s: RootState) => s.activeLocation);
    const isAdmin = useSelector((s: RootState) => s.auth.user?.role?.name === 'Admin');
    const setRestaurant = useCallback((id: string) => dispatch(selectRestaurant(id)), [dispatch]);
    const setLocation = useCallback((id: string | null) => dispatch(selectLocation(id)), [dispatch]);
    return { ...slice, isAdmin, setRestaurant, setLocation };
};

/** Loads the switcher's options and repairs a stale selection. Mounted once, in Layout. */
export const useLoadLocationContext = (): void => {
    const dispatch = useDispatch<AppDispatch>();
    const user = useSelector((s: RootState) => s.auth.user);
    const { restaurantId, locationId, restaurants, locations } = useSelector((s: RootState) => s.activeLocation);
    const isAdmin = user?.role?.name === 'Admin';
    const ownRestaurantId = user?.restaurant?.id ?? null;
    // A Staff or Manager assigned to a location works in that location only (the "one location" promise of the team form).
    const ownLocationId = isAdmin ? null : (user?.location?.id ?? null);

    // Admin: every restaurant. Others: their own, fixed.
    useEffect(() => {
        if (!user) return;
        if (!isAdmin) {
            if (ownRestaurantId && ownRestaurantId !== restaurantId) dispatch(selectRestaurant(ownRestaurantId));
            return;
        }
        let cancelled = false;
        api.get<ContextOption[]>('/restaurants')
            .then(({ data }) => {
                if (cancelled) return;
                dispatch(setRestaurants(data.map(({ id, name }) => ({ id, name }))));
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [user, isAdmin, ownRestaurantId, restaurantId, dispatch]);

    // Admin without a valid restaurant gets the first one.
    useEffect(() => {
        if (isAdmin && restaurants.length > 0 && !restaurants.some((r) => r.id === restaurantId)) {
            dispatch(selectRestaurant(restaurants[0].id));
        }
    }, [isAdmin, restaurants, restaurantId, dispatch]);

    // Locations of the selected restaurant.
    useEffect(() => {
        if (!restaurantId) return;
        let cancelled = false;
        api.get<ContextOption[]>(`/restaurants/${restaurantId}/locations`)
            .then(({ data }) => {
                if (cancelled) return;
                dispatch(
                    setLocationOptions(data.filter((l) => !ownLocationId || l.id === ownLocationId).map(({ id, name }) => ({ id, name })))
                );
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [restaurantId, ownLocationId, dispatch]);

    // Repair the selection: "all locations" (null) is only valid for a non-Admin with several locations.
    useEffect(() => {
        if (locations.length === 0) return;
        const valid =
            locationId === null ? !isAdmin && locations.length > 1 : locations.some((l) => l.id === locationId);
        if (!valid) dispatch(selectLocation(locations[0].id));
    }, [locations, locationId, isAdmin, dispatch]);
};
